"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import React, { useEffect, useMemo, useState } from "react";
import { loginPath } from "../../../i18n/paths";
import { useAuth } from "../../auth-client";
import { useHydrated } from "../../use-hydrated";
import {
  EMPTY_PLANNER_FILTERS,
  type PlannerFilters,
  type PlannerRace,
  type PlannerSort,
} from "./planner-contracts";
import {
  hasPlannerFilters,
  PlannerActiveFilters,
  PlannerMobileFilters,
} from "./planner-filters";
import { plannerRaceClipboardText } from "./planner-formatting";
import {
  plannerRaceQueryKey,
  usePlannerExportMutation,
  usePlannerQueries,
  useSavePlannerRaceMutation,
} from "./planner-queries";
import { PlannerResults } from "./planner-results";

export function PlannerWorkspace() {
  const locale = useLocale() === "ar" ? "ar" : "en";
  const t = useTranslations("Planner");
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [copyError, setCopyError] = useState(false);
  const [savedView, setSavedView] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<PlannerSort>("NEWEST");
  const [filters, setFilters] = useState<PlannerFilters | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<PlannerFilters | null>(
    null,
  );
  const hydrated = useHydrated();
  const {
    defaultFilters,
    places: placesQuery,
    queryFilters,
    races: racesQuery,
  } = usePlannerQueries({
    filters: appliedFilters,
    savedView,
    savedViewEnabled: auth.status === "authenticated",
    search,
    sort,
  });
  const draftFilters = filters ?? defaultFilters;
  const saveRace = useSavePlannerRaceMutation();
  const exportPdf = usePlannerExportMutation();
  const races = useMemo(
    () => racesQuery.data?.pages.flatMap((page) => page.races) ?? [],
    [racesQuery.data?.pages],
  );
  const totalCount = racesQuery.data?.pages[0]?.totalCount ?? 0;
  const error =
    placesQuery.isError || racesQuery.isError ? t("loadError") : null;
  const actionError = copyError
    ? t("copyError")
    : saveRace.error !== null
      ? t("saveError")
      : exportPdf.error === null
        ? null
        : t("exportError");

  useEffect(() => {
    if (copied === null) return;
    const timeoutId = window.setTimeout(() => setCopied(null), 1_800);
    return () => window.clearTimeout(timeoutId);
  }, [copied]);

  useEffect(() => {
    const timeoutId = window.setTimeout(
      () => setSearch(searchInput.trim()),
      300,
    );
    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  function openView(
    nextSavedView: boolean,
    nextFilters: PlannerFilters | null,
  ) {
    const resolvedFilters = nextFilters ?? defaultFilters;
    setCopyError(false);
    saveRace.reset();
    exportPdf.reset();
    setAppliedFilters(nextFilters);
    setSavedView(nextSavedView);
    void queryClient.invalidateQueries({
      queryKey: plannerRaceQueryKey(
        nextSavedView,
        resolvedFilters,
        search,
        sort,
      ),
      exact: true,
    });
  }

  function toggleSaved(race: PlannerRace) {
    if (auth.status === "loading" || auth.status === "unavailable") return;
    if (auth.status !== "authenticated") {
      window.location.assign(loginPath(locale, "/planner"));
      return;
    }
    setCopyError(false);
    exportPdf.reset();
    saveRace.mutate({ raceId: race.id, saved: !race.saved });
  }

  async function copyRace(race: PlannerRace) {
    setCopyError(false);
    try {
      await navigator.clipboard.writeText(
        plannerRaceClipboardText(race, locale, t),
      );
      setCopied(race.id);
    } catch {
      setCopyError(true);
    }
  }

  function startPdfExport() {
    if (races.length === 0) return;
    setCopyError(false);
    saveRace.reset();
    exportPdf.mutate({ races, filters: queryFilters, locale });
  }

  function removeFilter(field: keyof PlannerFilters) {
    const nextFilters: PlannerFilters =
      field === "jurisdiction"
        ? { ...queryFilters, jurisdiction: "", racecourse: "" }
        : { ...queryFilters, [field]: "" };
    setFilters(nextFilters);
    openView(false, nextFilters);
  }

  const filterProps = {
    filters: draftFilters,
    places: placesQuery.data ?? null,
    locale,
    onChange: setFilters,
    onSearch: () => {
      setFiltersOpen(false);
      openView(false, draftFilters);
    },
    onClear: () => {
      setFiltersOpen(false);
      setFilters(EMPTY_PLANNER_FILTERS);
      openView(false, EMPTY_PLANNER_FILTERS);
    },
  } as const;

  return (
    <div className="min-h-[calc(100dvh-var(--top-bar-height))] bg-canvas text-primary">
      <div className="mx-auto min-w-0 max-w-app px-[var(--space-page-inline)] pt-0 pb-4 sm:px-6 sm:py-6 lg:px-8">
        <PlannerResults
          races={races}
          locale={locale}
          savedView={savedView}
          authLoading={
            auth.status === "loading" || auth.status === "unavailable"
          }
          exporting={exportPdf.isPending}
          error={error}
          actionError={actionError}
          loading={
            placesQuery.isFetching ||
            (racesQuery.isFetching && !racesQuery.isFetchingNextPage)
          }
          loadingMore={racesQuery.isFetchingNextPage}
          copiedRaceId={copied}
          totalCount={totalCount}
          hasMore={racesQuery.hasNextPage && !racesQuery.isError}
          hasActiveFilters={hasPlannerFilters(queryFilters)}
          activeFilters={
            <PlannerActiveFilters
              filters={queryFilters}
              places={placesQuery.data ?? null}
              locale={locale}
              onClear={filterProps.onClear}
              onRemove={removeFilter}
            />
          }
          search={searchInput}
          sort={sort}
          mobileFilters={
            <PlannerMobileFilters
              {...filterProps}
              open={filtersOpen}
              disabled={!hydrated}
              onOpen={() => setFiltersOpen(true)}
              onClose={() => setFiltersOpen(false)}
            />
          }
          onShowResults={() => openView(false, appliedFilters)}
          onShowSaved={() => {
            if (auth.status === "unavailable") return;
            if (auth.status !== "authenticated") {
              window.location.assign(loginPath(locale, "/planner"));
              return;
            }
            openView(true, appliedFilters);
          }}
          onSearchChange={setSearchInput}
          onSortChange={setSort}
          onExport={startPdfExport}
          onRetry={() => {
            saveRace.reset();
            exportPdf.reset();
            void racesQuery.refetch();
          }}
          onCopy={(race) => void copyRace(race)}
          onSave={toggleSaved}
          onLoadMore={() => void racesQuery.fetchNextPage()}
        />
      </div>
    </div>
  );
}
